import { useState } from "react";
import moment from "jalali-moment";
import { toast } from "react-toastify";
import DownloadIcon from "@mui/icons-material/Download";
import { timeDifference } from "../../../../../utils/timeDifference";
import useMutation from "../../../../../hooks/useMutation";
import { assignmentService } from "../../../../../api/services/assignments";
import FileUploader from "../../../../shares/fileUploader/FileUplaoder";
import { StatusBadge } from "../../../../shares/dashboard/DashboardWidgets";

/**
 * An assignment that is still open for submission.
 *
 * The upload flow is untouched: picking a file POSTs it to
 * assignment-platform/:id/upload/, a 200 toasts and refetches the parent list,
 * a 400 surfaces the server's message. Only the layout changed - the old card
 * was a wrap-flex row where the deadline, status and two buttons all competed
 * at the same weight.
 */
const CurrentHW = ({ assignmentInfo, examsFetchData }) => {
    const [file, setFile] = useState(
        assignmentInfo.assignment_student_file ?? null
    );
    const upload = useMutation();
    const uploadFile = async (picked) => {
        if (upload.disabled) return;
        setFile(picked);
        if (!picked || typeof picked === "string") return;
        const response = await upload.run((signal) => assignmentService.upload(assignmentInfo.score.assignment, picked, signal));
        if (response) { setFile(null); toast.success("پاسخ تکلیف ارسال شد.", { rtl: true }); examsFetchData(); }
    };
    const uploadError = upload.error;

    const daysLeft = timeDifference(
        assignmentInfo.assignment_available_time_end,
        Date.now()
    );
    const tone = daysLeft <= 1 ? "warning" : daysLeft <= 3 ? "warning" : "primary";
    const deadlineLabel =
        daysLeft === 0
            ? "امروز آخرین مهلت"
            : `${Number(daysLeft).toLocaleString("fa-IR")} روز فرصت`;

    return (
        <article className="flex flex-col gap-lg rounded-panel border border-rule-subtle bg-surface p-xl shadow-card">
            <div className="flex flex-wrap items-start justify-between gap-md">
                <div className="min-w-0">
                    <h3 className="text-lg font-semibold text-ink">
                        {assignmentInfo.AssignmentName}
                    </h3>
                    {assignmentInfo.assignment_headline && (
                        <p className="mt-2xs text-base text-ink-muted">
                            {assignmentInfo.assignment_headline}
                        </p>
                    )}
                </div>
                <StatusBadge label={deadlineLabel} tone={tone} />
            </div>

            <div className="flex flex-wrap items-center gap-x-xl gap-y-2xs border-t border-rule-subtle pt-lg text-sm text-ink-subtle">
                <span>
                    مهلت تحویل:{" "}
                    <span className="font-medium tabular-nums text-ink">
                        {moment(
                            assignmentInfo.assignment_available_time_end,
                            "YYYY/MM/DD"
                        )
                            .locale("fa")
                            .format("DD MMMM YYYY")}
                    </span>
                </span>
                {assignmentInfo.assignment_filestatus && (
                    <span>{assignmentInfo.assignment_filestatus}</span>
                )}
            </div>

            <div className="flex flex-wrap items-center gap-sm">
                {assignmentInfo.assignment_file && (
                    <a
                        href={`${assignmentInfo.assignment_file}`}
                        download
                        className="inline-flex items-center gap-xs rounded-soft border border-rule px-lg py-xs text-sm text-ink-muted transition-colors duration-150 hover:bg-surface-sunken hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary motion-reduce:transition-none"
                    >
                        <DownloadIcon sx={{ fontSize: "1.8rem" }} />
                        دریافت صورت تکلیف
                    </a>
                )}
                {uploadError && <p role="alert" className="text-danger">{uploadError}</p>}
                <FileUploader
                    value={file}
                    onChange={uploadFile}
                    label={upload.busy ? "لطفا صبر کنید" : "ارسال پاسخ"}
                    className="py-6 border-primary"
                    showToast={false}
                    loading={upload.busy}
                    admin={false}
                />
            </div>
        </article>
    );
};

export default CurrentHW;
